// @flow
import * as React from 'react';

import assertions from 'lib/assertions';
import Button from 'components/Button.jsx';
import Container from 'components/Container.jsx';
import FormElement from 'components/FormElement.jsx';
import Heading from 'components/Heading.jsx';
import Input from 'components/Input.jsx';
import List from 'components/List.jsx';
import ListItem from 'components/ListItem.jsx';
import MediaObject from 'components/MediaObject.jsx';
import Row from 'components/Row.jsx';
import Text from 'components/Text.jsx';
import View from 'components/View.jsx';
import WizardHelperText from 'components/WizardHelperText';
import WizardPageLayout from 'components/WizardPageLayout';

import type { AutoMessageTabKey } from './';

type Props = {
  csvName: ?string,
  onChangeField: () => void,
  onAdvance: () => void,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  onJump: (key: AutoMessageTabKey) => void,
};

type State = {
  csvNameFromField: ?string,
};

class AdminNewAutoMessageFormatCSV extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      csvNameFromField: props.csvName,
    };
  }

  componentDidMount() {
    if (this.props.csvName) {
      this.setCsvNameField(this.props.csvName);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.csvName && this.props.csvName) {
      this.setCsvNameField(this.props.csvName);
    }
  }

  setCsvNameField = (csvName: ?string) => {
    this.setState({ csvNameFromField: csvName });
  };

  handleCsvNameChange = (csvName: ?string) => {
    this.setCsvNameField(csvName);
    this.props.onChangeField();
  };

  render() {
    const { onSave } = this.props;
    const { csvNameFromField } = this.state;

    return (
      <WizardPageLayout
        actionButtons={[
          <Button
            data-test="continue-button"
            onClick={() => onSave({ csvName: csvNameFromField }, this.props.onAdvance)}
            disabled={!csvNameFromField}>
            Save and continue
          </Button>,
        ]}>
        <View>
          <Row size="xlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Format export (CSV file) containing student IDs</Text>
                  </Heading>
                </Row>
                <MediaObject center={false} figure="right">
                  <Container flush size="large">
                    <List marker="number" size="xlarge" markerPosition="outside">
                      <ListItem>
                        <Row size="small">
                          Export a report containing the student IDs of the students you’d like to
                          reach (you’ll also be able to reach their parents and guardians). Your
                          report should include a column with the header{' '}
                          <Text element="code">student_id</Text>. Save your report as a CSV file.
                        </Row>
                        <Row size="small">
                          Every time the CSV file is transferred to Remind, the student IDs in the{' '}
                          <Text element="code">student_id</Text> column will be matched to rostered
                          students on Remind.
                        </Row>
                        <Button href="/assets/csv/student_id_list.csv" level="secondary">
                          Download template
                        </Button>
                      </ListItem>
                      <ListItem>
                        <Row size="small">Enter your report’s filename.</Row>
                        <Row size="small">
                          <Container size="small" flush align="left">
                            <FormElement
                              label="CSV filename"
                              onValidChange={e => this.handleCsvNameChange(e.target.value)}
                              onInvalidChange={() => this.handleCsvNameChange(undefined)}
                              validate={e => {
                                assertions.endsWithExactString(
                                  e.target.value,
                                  '.csv',
                                  'Your filename must include .csv at the end of it'
                                );
                              }}
                              element={Input}
                              elementProps={{
                                defaultValue: csvNameFromField,
                                placeholder: 'unexcusedabsences.csv, lowlunchbalance.csv',
                              }}
                            />
                          </Container>
                        </Row>
                        Once you activate this auto message, it will be sent every time a CSV file
                        with this unique filename is transferred to Remind.
                      </ListItem>
                    </List>
                  </Container>
                  <WizardHelperText
                    text="Not ready to export reports from your SIS?"
                    linkLabel="Skip to compose"
                    onClickLink={() => this.props.onJump('compose')}
                  />
                </MediaObject>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageFormatCSV;
